import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getAgent } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Copy, Check, ExternalLink, Code2, Eye, Loader2 } from 'lucide-react';
import AgentAvatar from '../components/AgentAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WidgetPreviewPage = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const res = await getAgent(agentId);
        setAgent(res.data);
        setPreviewUrl(`${API_URL}/api/widget/${agentId}/preview`);
      } catch (e) {
        console.error('Failed to fetch agent:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAgent();
  }, [agentId]);

  const embedCode = agent?.embed_code || `<!-- Apollo Chat Widget -->\n<script src="${API_URL}/api/widget/${agentId}/loader.js" async></script>`;

  const handleCopy = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!agent) {
    return (
      <Layout>
        <div className="p-8 text-center">
          <p className="text-muted-foreground">Agent not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/agents')}>Back to Agents</Button>
        </div>
      </Layout>
    );
  }

  const wc = agent.widget_config || {};
  const displayName = agent.display_name_effective || agent.display_name || agent.name;

  return (
    <Layout>
      <div className="h-screen flex flex-col" data-testid="widget-preview-page">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-card">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(`/agents/${agentId}`)} className="p-2 hover:bg-secondary rounded-lg transition-colors" data-testid="back-to-agent-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <AgentAvatar name={displayName} avatarUrl={agent.avatar_url} className="h-10 w-10 border border-border" fallbackClassName="bg-primary/10 text-primary font-semibold" />
              <div>
                <h1 className="text-lg font-semibold font-['Outfit'] text-foreground">Widget Distribution</h1>
                <p className="text-xs text-muted-foreground">{displayName}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={agent.is_active ? 'default' : 'secondary'}>
              {agent.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Embed Instructions */}
          <div className="w-[420px] border-r border-border overflow-y-auto bg-card p-6 space-y-6">
            
            {/* Embed Code */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Code2 className="w-4 h-4 text-primary" />
                <Label className="text-base font-semibold">Embed Code</Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Paste this snippet before the closing <code className="bg-secondary px-1.5 py-0.5 rounded text-[11px] font-mono">&lt;/body&gt;</code> tag of your website.
              </p>
              <div className="relative">
                <pre className="bg-secondary rounded-xl p-4 text-xs font-mono overflow-x-auto text-foreground leading-relaxed whitespace-pre-wrap break-all">
                  {embedCode}
                </pre>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="absolute top-2 right-2 rounded-lg gap-1.5 bg-card" 
                  onClick={handleCopy}
                  data-testid="copy-embed-btn"
                >
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>

            {/* Current Config Summary */}
            <div className="space-y-3 border-t border-border pt-5">
              <Label className="text-base font-semibold">Current Configuration</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-secondary/50 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Animation</p>
                  <p className="text-sm font-medium capitalize">{wc.bubble_animation || 'bounce'}</p>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Position</p>
                  <p className="text-sm font-medium capitalize">{(wc.position || 'bottom-right').replace('-', ' ')}</p>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Color</p>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border border-border" style={{ backgroundColor: wc.primary_color || '#9333EA' }} />
                    <p className="text-sm font-mono">{wc.primary_color || '#9333EA'}</p>
                  </div>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Avatar</p>
                  <p className="text-sm font-medium">{wc.show_avatar !== false ? 'Visible' : 'Hidden'}</p>
                </div>
              </div>
              {agent.greeting_message && (
                <div className="bg-secondary/50 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Greeting</p>
                  <p className="text-sm">{agent.greeting_message}</p>
                </div>
              )}
              <Button variant="outline" className="w-full rounded-lg gap-2" onClick={() => navigate(`/agents/${agentId}`)}>
                <Settings2Icon className="w-4 h-4" />
                Edit Widget Settings
              </Button>
            </div>

            {/* Integration Guide */}
            <div className="space-y-3 border-t border-border pt-5">
              <Label className="text-base font-semibold">Integration Guide</Label>
              <div className="space-y-4 text-xs text-muted-foreground">
                <div className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[11px] font-bold flex-shrink-0">1</span>
                  <div>
                    <p className="text-foreground font-medium mb-0.5">Copy the embed code</p>
                    <p>Click the copy button above to copy the widget script tag.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[11px] font-bold flex-shrink-0">2</span>
                  <div>
                    <p className="text-foreground font-medium mb-0.5">Paste into your HTML</p>
                    <p>Add the snippet before <code className="bg-secondary px-1 py-0.5 rounded font-mono">&lt;/body&gt;</code> on every page where you want the widget.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[11px] font-bold flex-shrink-0">3</span>
                  <div>
                    <p className="text-foreground font-medium mb-0.5">That's it!</p>
                    <p>The chat widget will appear automatically. No extra configuration needed.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Live Preview */}
          <div className="flex-1 flex flex-col bg-secondary/20">
            <div className="px-6 py-3 bg-card border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Live Preview</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                Widget is live
              </div>
            </div>
            <div className="flex-1 relative">
              <iframe
                src={`${API_URL}/api/widget/${agentId}/preview`}
                className="w-full h-full border-0"
                title="Widget Preview"
                data-testid="widget-preview-iframe"
              />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

// Small icon component to avoid import issues
const Settings2Icon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M20 7h-9"/><path d="M14 17H5"/><circle cx="17" cy="17" r="3"/><circle cx="7" cy="7" r="3"/>
  </svg>
);

export default WidgetPreviewPage;
